import { FolderZip, InsertDriveFile, Quiz, VideoFile } from '@mui/icons-material';
import { Box, BoxProps, Stack, StackProps, Tooltip, Typography } from '@mui/material';
import { downloadUrl } from 'helpers/files.helper';
import useAppSelector from 'hooks/useAppSelector';
import useLightbox from 'hooks/useLightbox';
import React from 'react';
import DocViewer, { DocViewerRenderers } from 'react-doc-viewer';
import ReactPlayer from 'react-player';
import Lightbox, {
    CustomSlideProperties,
    Pdf,
    RenderSlideProps,
    RenderThumbnailProps,
    Slide,
    SlideImage
} from 'yet-another-react-lightbox';
import Download from 'yet-another-react-lightbox/plugins/download';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import 'yet-another-react-lightbox/styles.css';

const LightboxProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const state = useAppSelector((state) => state.lightbox);

    const { remove, changeCurrentIndex } = useLightbox();

    const ShowThumbnails = state.slides.length == 1 ? () => {} : Thumbnails;

    const ref = React.useRef(null);

    return (
        <>
            {children}
            <Lightbox
                carousel={{ finite: true }}
                open={state.isOpen}
                slides={state.slides}
                index={state.initialIndex || 0}
                on={{ view: ({ index: currentIndex }) => changeCurrentIndex(currentIndex) }}
                download={{
                    // @ts-ignore
                    download: ({ slide }) => downloadUrl({ src: slide.src, name: slide.download.filename })
                }}
                close={remove}
                plugins={[Download, ShowThumbnails]}
                render={{
                    // @ts-ignore
                    slide: handleRender,
                    thumbnail: handlePreviewRender
                }}
            />
        </>
    );
};

type PreviewRenderProps = RenderThumbnailProps;

const iconSx = { color: 'white', width: 22, height: 22, ml: 0.5 };

const handlePreviewRender = ({ slide }: PreviewRenderProps) => {
    const typedSlide: Slide & CustomSlideProperties = slide as any;
    const splittedFileName = slide.src.split('/');
    const fileName = splittedFileName[splittedFileName.length - 1];

    if (typedSlide.subtype === 'pdf')
        return (
            <ThumbnailWrapper caption={fileName}>
                <Typography color="white" variant="h5" fontSize={24} textTransform={'uppercase'}>
                    {typedSlide.subtype}
                </Typography>
                <InsertDriveFile sx={{ ...iconSx }} />
            </ThumbnailWrapper>
        );
    else if (typedSlide.subtype === 'zip' || typedSlide.subtype === 'rar' || typedSlide.subtype === 'tar' || typedSlide.subtype === '7z')
        return (
            <ThumbnailWrapper caption={fileName}>
                <Typography color="white" variant="h5" fontSize={24} textTransform={'uppercase'}>
                    {typedSlide.subtype}
                </Typography>
                <FolderZip sx={{ ...iconSx }} />
            </ThumbnailWrapper>
        );
    else if (
        typedSlide.subtype === 'jpeg' ||
        typedSlide.subtype === 'png' ||
        typedSlide.subtype === 'jpg' ||
        typedSlide.subtype === 'svg+xml' ||
        typedSlide.subtype === 'gif' ||
        typedSlide.subtype === 'webp'
    )
        return (
            <SlideWrapper>
                <Box
                    component={'img'}
                    src={slide.src}
                    sx={{ height: { xs: 'auto', sm: '100%' }, width: { xs: '100%', sm: 'auto' }, objectFit: 'fit' }}
                />
            </SlideWrapper>
        );
    else if (typedSlide.subtype === 'mp4' || typedSlide.subtype === 'avi' || typedSlide.subtype === 'mov' || typedSlide.subtype === 'mkv')
        return (
            <ThumbnailWrapper caption={fileName}>
                <Typography color="white" variant="h5" fontSize={24} textTransform={'uppercase'}>
                    {typedSlide.subtype}
                </Typography>
                <VideoFile sx={{ ...iconSx }} />
            </ThumbnailWrapper>
        );
    // DOC, DOCX
    else if (
        typedSlide.subtype === 'vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        typedSlide.subtype === 'doc' ||
        typedSlide.subtype === 'msword' ||
        typedSlide.subtype === 'docx'
    )
        return (
            <ThumbnailWrapper caption={fileName}>
                <Typography color="white" variant="h5" fontSize={24} textTransform={'uppercase'}>
                    {typedSlide.subtype}
                </Typography>
                <InsertDriveFile sx={{ ...iconSx }} />
            </ThumbnailWrapper>
        );
    else
        return (
            <ThumbnailWrapper caption={fileName}>
                <Typography color="white" variant="h5" fontSize={24} textTransform={'uppercase'}>
                    {typedSlide.subtype}
                </Typography>
                <Quiz sx={{ ...iconSx }} />
            </ThumbnailWrapper>
        );
};

type RenderProps = RenderSlideProps<Slide & CustomSlideProperties>;

const handleRender = ({ slide }: RenderProps) => {
    const splittedUrl = slide.src.split('/');
    const fileName = splittedUrl[splittedUrl.length - 1];
    if (slide.subtype === 'pdf') return <PdfPreview slide={slide as Pdf} />;
    else if (
        slide.subtype === 'jpeg' ||
        slide.subtype === 'png' ||
        slide.subtype === 'jpg' ||
        slide.subtype === 'svg+xml' ||
        slide.subtype === 'gif' ||
        slide.subtype === 'webp'
    )
        return <ImagePreview slide={slide as SlideImage} />;
    else if (slide.subtype === 'mp4' || slide.subtype === 'avi' || slide.subtype === 'mov' || slide.subtype === 'mkv')
        return <VideoPreview url={slide.src} />;
    // DOC, DOCX
    else if (
        slide.subtype === 'vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        slide.subtype === 'doc' ||
        slide.subtype === 'msword' ||
        slide.subtype === 'docx'
    )
        return <DocPreview url={slide.src} />;
    else
        return (
            <Stack sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle1" color="white">
                    {fileName}
                </Typography>
                <Typography variant="subtitle2" color="white">
                    File Type {slide.subtype} is not supported
                </Typography>
                <Typography variant="subtitle2" color="white">
                    You can download it by clicking icon in top right corner.
                </Typography>
            </Stack>
        );
};

const SlideWrapper: React.FC<React.PropsWithChildren & BoxProps> = ({ children, ...props }) => {
    return (
        <Box {...props} sx={{ height: { xs: 'auto', sm: '100%' }, width: 'auto', ...props.sx }}>
            {children}
        </Box>
    );
};

const ThumbnailWrapper: React.FC<React.PropsWithChildren & StackProps & { caption: string }> = ({ children, caption, ...props }) => {
    return (
        <Tooltip
            title={caption}
            sx={{}}
            PopperProps={{
                sx: {
                    zIndex: 10000,
                    maxWidth: 130
                },
                onClick: (event) => event.stopPropagation()
            }}
        >
            <Stack {...props} sx={{ width: '100%', height: '100%', justifyContent: 'space-evenly', ...props.sx }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>{children}</Box>

                <Typography
                    sx={{
                        color: 'white',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        marginLeft: caption.length > 19 ? 1 : 0
                    }}
                    variant="caption"
                >
                    {caption}
                </Typography>
            </Stack>
        </Tooltip>
    );
};

interface PdfProps {
    slide: Pdf;
}

const PdfPreview = ({ slide }: PdfProps) => {
    return (
        <SlideWrapper>
            <Box component={'iframe'} src={slide.src} key={slide.id} sx={{ width: '88vw', height: '90vh' }} />
        </SlideWrapper>
    );
};

interface DocProps {
    url: string;
}

const DocPreview = ({ url }: DocProps) => {
    return (
        <SlideWrapper>
            <Box
                component={DocViewer}
                documents={[{ uri: url }]}
                pluginRenderers={DocViewerRenderers}
                sx={{ width: '88vw', height: '90vh' }}
            />
        </SlideWrapper>
    );
};

interface ImageProps {
    slide: SlideImage;
}

const ImagePreview = ({ slide }: ImageProps) => {
    return (
        <SlideWrapper>
            <Box
                component={'img'}
                src={slide.src}
                sx={{ height: { xs: 'auto', sm: '100%' }, width: { xs: '100%', sm: 'auto' }, objectFit: 'cover' }}
            />
        </SlideWrapper>
    );
};

interface VideoProps {
    url: string;
}

const VideoPreview = ({ url }: VideoProps) => {
    return (
        <SlideWrapper sx={{ display: 'flex', alignItems: 'center' }}>
            <Box component={ReactPlayer} controls={true} url={url} width={900} height={1600} />
        </SlideWrapper>
    );
};

export default LightboxProvider;
